import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle } from '../../styles/StyledComponents';

// Import testimonial images
import marcImage from '../../assets/images/testimonials/marc.jpeg';
import seamusImage from '../../assets/images/testimonials/seamus.jpeg';
import drewImage from '../../assets/images/testimonials/drew.jpeg';
import joshImage from '../../assets/images/testimonials/josh.jpeg';
import julienImage from '../../assets/images/testimonials/julien.jpeg';
import bennyImage from '../../assets/images/testimonials/benny.jpeg';

const TestimonialsSection = styled.section`
  padding: 80px 0;
  background-color: ${({ theme }) => theme.colors.lightBg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 60px 0;
  }
`;

const TestimonialsContainer = styled(Container)`
  text-align: center;
`;

const TestimonialCarousel = styled.div`
  position: relative;
  margin-top: 60px;
  padding: 0 60px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

const TestimonialWrapper = styled.div`
  position: relative;
  min-height: ${({ height }) => height}px;
  margin-bottom: 60px; /* Add extra margin at the bottom to prevent overflow */
`;

const TestimonialCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
  max-width: 800px;
  text-align: left;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: ${({ active }) => (active ? 1 : 0)};
  z-index: ${({ active }) => (active ? 2 : 1)};
  pointer-events: ${({ active }) => (active ? 'all' : 'none')};
  transition: opacity 0.5s ease;
`;

const Quote = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 20px;
  font-style: italic;
  position: relative;
  
  /* Removing the quotation mark that appears in the background */
  /*
  &::before {
    content: '"';
    font-size: 4rem;
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.2;
    position: absolute;
    top: -20px;
    left: -15px;
  }
  */
`;

const Author = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div`
`;

const AuthorName = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
`;

const AuthorTitle = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 5px 0 0;
`;

const CarouselNav = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  position: absolute;
  bottom: -50px;
  left: 0;
  right: 0;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ active, theme }) => 
    active ? theme.colors.primary : 'rgba(240, 90, 40, 0.2)'};
  border: none;
  margin: 0 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${({ active, theme }) => 
      active ? theme.colors.primary : 'rgba(240, 90, 40, 0.4)'};
  }
  
  &:focus {
    outline: none;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ direction }) => (direction === 'prev' ? 'left: 10px;' : 'right: 10px;')}
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(240, 90, 40, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
  }
`;

// Real testimonials data with images
const testimonials = [
  {
    id: 1,
    quote: "I had the pleasure of collaborating with Scott's company on developing an AI application tailored for higher education. Throughout our partnership, Scott consistently demonstrated exceptional expertise and willingness to go the extra mile to make sure it met my expectations. His pragmatic approach and understanding of AI technologies were instrumental in navigating some of the challenges of delivering a solution that met my requirements. Beyond his technical acumen, Scott is a great communicator, which makes him not only a valued colleague but also a good collaborator. I wholeheartedly recommend Scott to any organization seeking a visionary and compassionate leader in the tech industry.",
    name: "Marc Macaluso",
    title: "Digital Transformation Consultant",
    image: marcImage
  },
  {
    id: 2,
    quote: "Scott's expertise in AI automation was invaluable in helping me implement it seamlessly into my small business. He guided me through the process with great attention to detail, making complex concepts easy to understand. His clear communication and deep knowledge of AI allowed me to quickly grasp how to use automation effectively and its potential to transform my business. After implementing Scott's recommendations, I saw an immediate increase in lead activity, which has significantly boosted my ability to serve clients efficiently. I highly recommend working with Scott—his insights and guidance will make a meaningful difference for your business.",
    name: "Seamus Hogan",
    title: "Personal Fitness Coach | Holistic Life Coach",
    image: seamusImage
  },
  {
    id: 3,
    quote: "I had what I hoped would be a brilliant idea for leveraging AI for top of funnel lead generation. One big hurdle, ChatGPT was not playing nice and I am not a programmer. At the Cincy AI for Humans meetup, I put out a request for help. Up walks Scott Laughlin. He asked a lot of pertinent questions that gave me the sense that he truly understood what I was trying to accomplish. He delivered my GPT-backed project on time and on budget. I needed a quick proof of concept of my idea and he delivered more than I had hoped for and was very easy to work with. Will hire again. Highly recommend that you have a discussion with Scott. He'll tell you if he can help you or not.",
    name: "Drew Dinkelacker",
    title: "Fractional CMO / Author / Podcaster",
    image: drewImage
  },
  {
    id: 4,
    quote: "I highly recommend Scott for his exceptional technical expertise, professionalism, and collaborative spirit. He asks insightful questions to fully understand challenges and consistently delivers thoughtful, effective solutions. A true professional and an excellent partner to work with!",
    name: "Josh Adler",
    title: "Product Enthusiast, Thoughtful Teammate",
    image: joshImage
  },
  {
    id: 5,
    quote: "Scott is someone that gets things done! He will help you with your project and surround himself with the right team to make it happen. I am happy with their work.",
    name: "Julien Chosson",
    title: "Technology Entrepreneur",
    image: julienImage
  },
  {
    id: 6,
    quote: "It was pleasure working with Scott. He is very proactive and innovative in his work. I will surely work again with him. Highly recommended.",
    name: "Benny Gala",
    title: "Private Equity Advisor",
    image: bennyImage
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardHeight, setCardHeight] = useState(300); // Default height
  const testimonialRefs = useRef([]);
  
  // Initialize refs array
  useEffect(() => {
    testimonialRefs.current = testimonialRefs.current.slice(0, testimonials.length);
  }, []);
  
  // Function to handle next testimonial
  const nextTestimonial = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  // Function to handle previous testimonial
  const prevTestimonial = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };
  
  // Function to handle dot navigation
  const goToTestimonial = (index) => {
    setActiveIndex(index);
  };
  
  // Auto-rotate testimonials every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Update card height based on content
  useEffect(() => {
    const updateCardHeight = () => {
      const activeCard = testimonialRefs.current[activeIndex];
      if (activeCard) {
        setCardHeight(activeCard.offsetHeight);
      }
    };
    
    updateCardHeight();
    window.addEventListener('resize', updateCardHeight);
    
    return () => window.removeEventListener('resize', updateCardHeight);
  }, [activeIndex]);

  return (
    <TestimonialsSection id="testimonials">
      <TestimonialsContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Trusted by Leaders Across Industries
        </SectionTitle>
        <SectionSubtitle
          as={motion.p}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          See what our clients have to say about their experience with TechRamp
        </SectionSubtitle>
        
        <TestimonialCarousel>
          <TestimonialWrapper height={cardHeight}>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={testimonial.id}
                active={index === activeIndex}
                className={`testimonial-card ${index === activeIndex ? 'active' : ''}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: index === activeIndex ? 1 : 0 }}
                transition={{ duration: 0.5 }}
                ref={el => testimonialRefs.current[index] = el}
              >
                <Quote>{testimonial.quote}</Quote>
                <Author>
                  <AuthorImage>
                    <img src={testimonial.image} alt={testimonial.name} />
                  </AuthorImage>
                  <AuthorInfo>
                    <AuthorName>{testimonial.name}</AuthorName>
                    <AuthorTitle>{testimonial.title}</AuthorTitle>
                  </AuthorInfo>
                </Author>
              </TestimonialCard>
            ))}
          </TestimonialWrapper>
          
          <CarouselButton 
            direction="prev" 
            onClick={prevTestimonial}
            aria-label="Previous testimonial"
          >
            &#8592;
          </CarouselButton>
          <CarouselButton 
            direction="next" 
            onClick={nextTestimonial}
            aria-label="Next testimonial"
          >
            &#8594;
          </CarouselButton>
          
          <CarouselNav>
            {testimonials.map((_, index) => (
              <CarouselDot 
                key={index}
                active={index === activeIndex}
                onClick={() => goToTestimonial(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </CarouselNav>
        </TestimonialCarousel>
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

export default Testimonials;
